import { useRouter } from '@cp/shared/router';
import React, { PropsWithChildren, Reducer, useCallback, useEffect, useReducer } from 'react';
import { URN } from '@youhelp-ru/urn';
import { useUpdateQueryParams } from '@cp/shared/queryParams';
import { useEventBus, useEventBusSubscribe } from 'src/shared/eventBus';
import { useModal } from 'src/shared/modalProvider';
import { useStartBillingSubscription } from 'src/features/billing';
import { Intent, IntentConfig, IntentRunnerComponent, useIntentionService } from 'src/shared/intent';
import { featureRegistry } from './featureRegistry';

type ActionEvent = { name: 'action'; action: string; params: Iterable<[string, string]> };

export const ModalRunner = (props: PropsWithChildren) => {
  const subscribe = useEventBusSubscribe<ActionEvent>();
  const { openModal, closeModal } = useModal();
  const updateQueryParams = useUpdateQueryParams();

  const dropActionParam = useCallback(
    (name: string) => {
      updateQueryParams({ action: undefined });
      closeModal(name);
    },
    [updateQueryParams, closeModal],
  );

  useEffect(() => {
    return subscribe({ name: 'action' }, (event) => {
      const { action, params } = event;
      if (!URN.canParse(action)) {
        return;
      }
      const urn = new URN(action);
      if (urn.nid !== 'modal') {
        return;
      }
      const modalName = urn.nss;
      const mp = Object.fromEntries(params);
      const onClose = dropActionParam.bind(null, modalName);
      // @ts-expect-error modalProps should be type coerced
      openModal(modalName, { ...mp, onModalDestroy: onClose, onCancel: onClose, onSuccess: onClose });
    });
  }, [subscribe, dropActionParam, openModal]);
  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{props.children}</>;
};

type RunnerState = {
  Component: IntentRunnerComponent;
  intent: Intent | null;
};
const EmptyComponent: IntentRunnerComponent = () => null;
const initialState: RunnerState = {
  intent: null,
  Component: EmptyComponent,
};

type RunnerAction = { type: 'start'; intent: Intent; config: IntentConfig } | { type: 'clear' };
const reducer: Reducer<RunnerState, RunnerAction> = (prevState, action) => {
  switch (action.type) {
    case 'start':
      // Do not run feature while another one is in progress;
      if (prevState.intent) {
        return prevState;
      } else {
        return {
          intent: action.intent,
          Component: action.config.Component,
        };
      }
    case 'clear':
      return initialState;
    default:
      return prevState;
  }
};

const FeatureRunner = () => {
  const intentService = useIntentionService();
  const [state, dispatch] = useReducer(reducer, initialState);
  const Component = state.Component;
  const onSuccess = useCallback(() => dispatch({ type: 'clear' }), [dispatch]);
  const onError = useCallback(() => dispatch({ type: 'clear' }), [dispatch]);

  useEffect(() => {
    const intent = intentService.pickIntent();
    if (!intent) {
      return;
    }
    const loader = featureRegistry[intent.action];
    if (!loader) {
      console.warn('Cant find feature for action: ', intent.action);
    }
    loader().then((config) => dispatch({ type: 'start', intent, config }));
  }, [dispatch, intentService]);

  return (
    <React.Suspense>{state.intent ? <Component intent={state.intent} onError={onError} onSuccess={onSuccess} /> : null}</React.Suspense>
  );
};
FeatureRunner.displayName = 'FeatureRunner';

export const ActionRunner = () => {
  const router = useRouter();
  const eventBus = useEventBus();

  useEffect(() => {
    const url = new URL(router.location.href);
    if (url.searchParams.has('action')) {
      eventBus.publish({ name: 'action', action: url.searchParams.get('action'), params: url.searchParams.entries() });
    }
  }, [router.location, eventBus]);

  return (
    <ModalRunner>
      <FeatureRunner />
    </ModalRunner>
  );
};
ActionRunner.displayName = 'ActionRunner';
